import Clarity from '@microsoft/clarity';

class ClarityService {
  private isInitialized = false;
  private currentSessionId: string | null = null;
  private currentUserId: string | null = null;
  private hasBeenInitialized = false;

  /**
   * Initialize Clarity with project ID and set up session tracking
   */
  init(projectId: string): void {
    try {
      Clarity.init(projectId);
      this.isInitialized = true;
      console.log('Microsoft Clarity initialized successfully');
      
      // Grant cookie consent for session tracking across pages
      // Per Microsoft documentation: required for multi-page session linking
      Clarity.consent();
      console.log('Microsoft Clarity cookie consent granted');
      
      // Initialize session tracking
      this.initializeSession();
    } catch (error) {
      console.error('Failed to initialize Microsoft Clarity:', error);
    }
  }

  /**
   * Initialize session tracking with persistent identifiers
   */
  private initializeSession(): void {
    if (!this.isInitialized || this.hasBeenInitialized) return;

    // Get or create persistent session ID
    this.currentSessionId = this.getOrCreateSessionId();
    
    // Get or create persistent anonymous user ID
    const anonymousUserId = this.getOrCreateAnonymousUserId();
    
    // Initial identify call with session tracking
    // Per Clarity docs: identify should be called on each page for optimal tracking
    this.identify(anonymousUserId, this.currentSessionId, this.getCurrentPageId());
    this.hasBeenInitialized = true;
  }

  /**
   * Get or create a session ID that persists for the browser session
   */
  private getOrCreateSessionId(): string {
    const key = 'clarity_session_id';
    let sessionId = sessionStorage.getItem(key);
    
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem(key, sessionId);
    }
    
    return sessionId;
  }

  /**
   * Get or create an anonymous user ID that persists across sessions
   */
  private getOrCreateAnonymousUserId(): string {
    const key = 'clarity_anonymous_user_id';
    let userId = localStorage.getItem(key);
    
    if (!userId) {
      userId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem(key, userId);
    }
    
    return userId;
  }

  /**
   * Get current page identifier
   */
  private getCurrentPageId(): string {
    return window.location.pathname;
  }

  /**
   * Identify a user with custom identifiers
   * @param customId - Required unique identifier for the customer
   * @param customSessionId - Optional custom session identifier  
   * @param customPageId - Optional custom page identifier
   * @param friendlyName - Optional friendly name for the customer
   */
  identify(
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string
  ): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized. Call init() first.');
      return;
    }

    try {
      // Use current session if not provided
      const sessionId = customSessionId || this.currentSessionId || undefined;
      const pageId = customPageId || this.getCurrentPageId();
      
      this.currentUserId = customId;
      
      Clarity.identify(customId, sessionId, pageId, friendlyName);
      console.log('Clarity user identified:', { customId, sessionId, pageId, friendlyName });
    } catch (error) {
      console.error('Failed to identify user in Clarity:', error);
    }
  }

  /**
   * Identify authenticated user while maintaining session continuity
   * @param userId - The authenticated user ID
   * @param email - User email for friendly name
   * @param additionalData - Additional user data for tags
   */
  identifyAuthenticatedUser(userId: string, email?: string, additionalData?: Record<string, any>): void {
    if (!this.isInitialized) return;

    // Update the user identification while keeping the same session ID
    // This upgrades the session from anonymous to authenticated
    this.identify(userId, this.currentSessionId || undefined, this.getCurrentPageId(), email);
    
    // Always set additional tags for latest user data
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        this.setTag(key, value);
      });
    }
    
    // Track authentication event
    this.event('user-authenticated');
  }

  /**
   * Track page navigation by re-identifying with same session ID (as per official Clarity docs)
   * @param pagePath - The page path being navigated to
   */
  trackPageNavigation(pagePath: string): void {
    if (!this.isInitialized) return;

    // Per official Microsoft Clarity documentation:
    // "For optimal user tracking, the Identify API should be called for each page of the website"
    // Session continuity is maintained by using the SAME session ID across all pages
    
    if (this.currentUserId) {
      // For authenticated users, re-identify with same session ID but new page ID
      this.identify(this.currentUserId, this.currentSessionId || undefined, pagePath);
    } else {
      // For anonymous users, re-identify with same session ID
      const anonymousUserId = this.getOrCreateAnonymousUserId();
      this.identify(anonymousUserId, this.currentSessionId || undefined, pagePath);
    }
    
    // Also track as custom event and set tags for additional context
    this.event(`page-view:${pagePath}`);
    this.setTag('current-page', pagePath);
    this.setTag('page-timestamp', new Date().toISOString());
  }

  /**
   * Handle user logout - reset to anonymous tracking
   */
  logout(): void {
    if (!this.isInitialized) return;

    // Track logout event
    this.event('user-logged-out');
    
    // Reset user state but keep session for continuity
    this.currentUserId = null;
    
    // Set tags for logged out state
    this.setTag('user-state', 'anonymous');
    this.setTag('logout-timestamp', new Date().toISOString());
    
    // Note: We don't reset initialization state as session continues
    // The session continues as anonymous with proper identify calls on each page
  }

  /**
   * Set custom tags for filtering and analysis
   * @param key - The key for the tag
   * @param value - The value(s) for the tag
   */
  setTag(key: string, value: string | string[]): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized. Call init() first.');
      return;
    }

    try {
      Clarity.setTag(key, value);
    } catch (error) {
      console.error('Failed to set tag in Clarity:', error);
    }
  }

  /**
   * Track custom events
   * @param eventName - The name of the event to track
   */
  event(eventName: string): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized. Call init() first.');
      return;
    }

    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error('Failed to track event in Clarity:', error);
    }
  }

  /**
   * Set cookie consent
   * @param consent - Whether consent is given (default: true)
   */
  consent(consent: boolean = true): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized. Call init() first.');
      return;
    }

    try {
      Clarity.consent(consent);
    } catch (error) {
      console.error('Failed to set consent in Clarity:', error);
    }
  }

  /**
   * Upgrade session priority for recording
   * @param reason - The reason for the upgrade
   */
  upgrade(reason: string): void {
    if (!this.isInitialized) {
      console.warn('Clarity not initialized. Call init() first.');
      return;
    }

    try {
      Clarity.upgrade(reason);
    } catch (error) {
      console.error('Failed to upgrade session in Clarity:', error);
    }
  }

  /**
   * Check if Clarity is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current session ID
   */
  get sessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Get current user ID
   */
  get userId(): string | null {
    return this.currentUserId;
  }
}

// Export singleton instance
export const clarityService = new ClarityService(); 